import { useEffect, useMemo, useState } from "react";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { ApiHelper, RmaApiHelper } from "api/ApiHelper";

import {
  calculateAverageTemperature,
  calculateCumulative,
  calculateTotalPrecipitation,
  formatWeatherDataForChart,
  getCurrentGrowthStage,
  getDroughtSeverityLabel,
  getGDDFieldName,
  WEATHER_FIELDS,
  WEATHER_NAMESPACE,
  WEATHER_OBJECT_NAME,
} from "./WeatherHelper";

export const useWeatherReport = ({
  dateRange,
  locationState,
  currentYear,
  selectedCropType,
  globalState,
  enqueueSnackbar,
  doLoadPriorYear = false,
}) => {
  // State for weather data
  const [weatherData, setWeatherData] = useState([]);
  const [isLoadingWeather, setIsLoadingWeather] = useState(false);

  // State for yield forecast data
  const [yieldForecastData, setYieldForecastData] = useState([]);
  const [isLoadingYieldForecast, setIsLoadingYieldForecast] = useState(false);

  // State for county data
  const [countyDataIrrigated, setCountyDataIrrigated] = useState(null);
  const [countyDataNonIrrigated, setCountyDataNonIrrigated] = useState(null);
  const [isLoadingCountyData, setIsLoadingCountyData] = useState(false);

  // State for historical yield trend data
  const [historicalYieldTrendDataIrrigated, setHistoricalYieldTrendDataIrrigated] = useState(null);
  const [historicalYieldTrendDataNonIrrigated, setHistoricalYieldTrendDataNonIrrigated] = useState(null);
  const [isLoadingHistoricalYieldTrend, setIsLoadingHistoricalYieldTrend] = useState(false);

  // Common error handler
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err?.detail || err?.message || JSON.stringify(err), {
      preventDuplicate: true,
      variant: "error",
    });
  };

  // Load weather data from API
  const loadWeatherData = async () => {
    if (!locationState.state_name || !locationState.county_name || !locationState.sub_county_code) {
      setWeatherData([]);
      return;
    }

    setIsLoadingWeather(true);
    try {
      const weatherParams = {
        state_name: locationState.state_name,
        county_name: locationState.county_name,
        sub_county_code: locationState.sub_county_code,
        year: currentYear,
      };

      const response = await ApiHelper.selectObject(WEATHER_NAMESPACE, WEATHER_OBJECT_NAME, weatherParams);

      if (doLoadPriorYear) {
        const priorYear = currentYear - 1;
        const priorYearWeatherParams = {
          state_name: locationState.state_name,
          county_name: locationState.county_name,
          sub_county_code: locationState.sub_county_code,
          year: priorYear,
        };
        const priorYearResponse = await ApiHelper.selectObject(
          WEATHER_NAMESPACE,
          WEATHER_OBJECT_NAME,
          priorYearWeatherParams
        );

        // Foreach response (current year), add a "_prior" field for Temp, Precip, GDD
        const priorYearData = response.map((item, index) => {
          // Prior Item, lookup by index:
          const priorItem = priorYearResponse[index];
          if (priorItem) {
            return {
              ...item,
              [WEATHER_FIELDS.MAX_TEMP_PRIOR]: priorItem[WEATHER_FIELDS.MAX_TEMP],
              [WEATHER_FIELDS.MIN_TEMP_PRIOR]: priorItem[WEATHER_FIELDS.MIN_TEMP],
              [WEATHER_FIELDS.PRECIPITATION_PRIOR]: priorItem[WEATHER_FIELDS.PRECIPITATION] / 25.4,
              [WEATHER_FIELDS.GDD_CORN_PRIOR]: priorItem[WEATHER_FIELDS.GDD_CORN],
              [WEATHER_FIELDS.GDD_SOYBEANS_PRIOR]: priorItem[WEATHER_FIELDS.GDD_SOYBEANS],
              [WEATHER_FIELDS.GDD_SORGHUM_PRIOR]: priorItem[WEATHER_FIELDS.GDD_SORGHUM],
              [WEATHER_FIELDS.GDD_WHEAT_PRIOR]: priorItem[WEATHER_FIELDS.GDD_WHEAT],
              [WEATHER_FIELDS.GDD_COTTON_PRIOR]: priorItem[WEATHER_FIELDS.GDD_COTTON],
              [WEATHER_FIELDS.GDD_RICE_PRIOR]: priorItem[WEATHER_FIELDS.GDD_RICE],
              [WEATHER_FIELDS.PDSI_PRIOR]: priorItem[WEATHER_FIELDS.PDSI],
              [WEATHER_FIELDS.Z_INDEX_PRIOR]: priorItem[WEATHER_FIELDS.Z_INDEX],
            };
          } else {
            return item;
          }
        });

        if (priorYearData?.length) {
          setWeatherData(priorYearData);
        } else {
          setWeatherData([]);
        }
      } else {
        setWeatherData(response);
      }
    } catch (err) {
      handleError(err);
      setWeatherData([]);
    } finally {
      setIsLoadingWeather(false);
    }
  };

  // Load yield forecast data from API
  const loadYieldForecastData = async () => {
    if (!locationState.state_name || !locationState.county_name) {
      setYieldForecastData([]);
      return;
    }

    setIsLoadingYieldForecast(true);
    try {
      const yieldForecastParams = {
        state_name: locationState.state_name,
        county_name: locationState.county_name,
        crop_year: currentYear,
        commodity_name: selectedCropType,
      };

      const response = await ApiHelper.callSmociObject(
        "data/DATANAC",
        "Gridmet",
        "YieldForecast",
        "Search",
        yieldForecastParams,
        globalState
      );

      if (response?.length) {
        setYieldForecastData(response);
      } else {
        setYieldForecastData([]);
      }
    } catch (err) {
      handleError(err);
      setYieldForecastData([]);
    } finally {
      setIsLoadingYieldForecast(false);
    }
  };

  // Load county data from API
  const loadCountyData = async () => {
    if (!locationState.state_name || !locationState.county_name) {
      setCountyDataIrrigated(null);
      setCountyDataNonIrrigated(null);
      return;
    }

    setIsLoadingCountyData(true);
    try {
      const rmaParams = {
        state_name: locationState.state_name,
        county_name: locationState.county_name,
        reinsurance_year: currentYear,
        commodity_name: selectedCropType,
      };

      const response = await RmaApiHelper.selectCountyData(rmaParams);

      // Search and filter:
      // if CORN, type_name="Grain"
      // other commodities, allow all
      const filteredResponse = response.filter((item) => {
        if (selectedCropType === "corn") {
          return item.type_name === "Grain";
        }
        return true;
      });

      // Find the first row for contains(Non-Irrigated) and the first for contains(" Irrigated" or ==="Irrigated")
      const nonIrrigated = filteredResponse.find((item) => item.practice_name.includes("Non-Irrigated"));
      const irrigated = filteredResponse.find(
        (item) => item.practice_name.includes("Irrigated") && !item.practice_name.includes("Non-Irrigated")
      );

      if (filteredResponse?.length) {
        setCountyDataIrrigated(irrigated);
        setCountyDataNonIrrigated(nonIrrigated);
      } else {
        setCountyDataIrrigated(null);
        setCountyDataNonIrrigated(null);
      }
    } catch (err) {
      handleError(err);
      setCountyDataIrrigated(null);
      setCountyDataNonIrrigated(null);
    } finally {
      setIsLoadingCountyData(false);
    }
  };

  // Load historical yield trend data from API
  const loadHistoricalYieldTrend = async () => {
    if (!locationState.state_name || !locationState.county_name) {
      setHistoricalYieldTrendDataIrrigated(null);
      setHistoricalYieldTrendDataNonIrrigated(null);
      return;
    }

    setIsLoadingHistoricalYieldTrend(true);
    try {
      const historicalYieldParams = {
        state_name: locationState.state_name,
        county_name: locationState.county_name,
        commodity_name: selectedCropType,
        yield_year: currentYear,
        reinsurance_year: getCurrentReinsuranceYear(),
      };

      const response = await RmaApiHelper.selectHistoricalYieldTrend(historicalYieldParams);

      // Search and filter:
      // if CORN, type_name="Grain"
      // other commodities, allow all
      const filteredResponse = response.filter((item) => {
        if (selectedCropType === "corn") {
          return item.type_name === "Grain";
        }
        return true;
      });

      // Find the first row for contains(Non-Irrigated) and the first for contains(" Irrigated" or ==="Irrigated")
      const nonIrrigated = filteredResponse.find((item) => item.practice_name.includes("Non-Irrigated"));
      const irrigated = filteredResponse.find(
        (item) => item.practice_name.includes("Irrigated") && !item.practice_name.includes("Non-Irrigated")
      );

      if (filteredResponse?.length) {
        setHistoricalYieldTrendDataIrrigated(irrigated);
        setHistoricalYieldTrendDataNonIrrigated(nonIrrigated);
      } else {
        setHistoricalYieldTrendDataIrrigated(null);
        setHistoricalYieldTrendDataNonIrrigated(null);
      }
    } catch (err) {
      handleError(err);
      setHistoricalYieldTrendDataIrrigated(null);
      setHistoricalYieldTrendDataNonIrrigated(null);
    } finally {
      setIsLoadingHistoricalYieldTrend(false);
    }
  };

  // Load data when parameters change
  useEffect(() => {
    if (locationState.state_name && locationState.county_name && locationState.sub_county_code) {
      loadWeatherData();
    }
    if (locationState.state_name && locationState.county_name) {
      loadCountyData();
      loadHistoricalYieldTrend();
      loadYieldForecastData();
    }
  }, [
    locationState.state_name,
    locationState.county_name,
    locationState.sub_county_code,
    currentYear,
    selectedCropType,
  ]);

  // Process weather data for charts
  const temperatureChartData = useMemo(() => {
    const data = formatWeatherDataForChart(weatherData, WEATHER_FIELDS.MAX_TEMP, dateRange);
    const returnValue = data.map((item) => ({
      ...item,
      [WEATHER_FIELDS.MAX_TEMP]: item[WEATHER_FIELDS.MAX_TEMP],
      [WEATHER_FIELDS.MIN_TEMP]: item[WEATHER_FIELDS.MIN_TEMP],
    }));

    return returnValue;
  }, [weatherData, dateRange]);

  const precipitationChartData = useMemo(() => {
    const data = formatWeatherDataForChart(weatherData, WEATHER_FIELDS.PRECIPITATION, dateRange);
    // Convert precipitation from mm to inches (1 inch = 25.4 mm)
    return data.map((item) => ({
      ...item,
      [WEATHER_FIELDS.PRECIPITATION]: (item[WEATHER_FIELDS.PRECIPITATION] || 0) / 25.4,
    }));
  }, [weatherData, dateRange]);

  const windChartData = useMemo(() => {
    return formatWeatherDataForChart(weatherData, WEATHER_FIELDS.WIND_SPEED, dateRange);
  }, [weatherData, dateRange]);

  const droughtChartData = useMemo(() => {
    const data = formatWeatherDataForChart(weatherData, WEATHER_FIELDS.Z_INDEX, dateRange);
    return data.map((item) => ({
      ...item,
      [WEATHER_FIELDS.PDSI]: item[WEATHER_FIELDS.PDSI],
      [WEATHER_FIELDS.Z_INDEX]: item[WEATHER_FIELDS.Z_INDEX],
    }));
  }, [weatherData, dateRange]);

  const gddChartData = useMemo(() => {
    const fieldName = getGDDFieldName(selectedCropType);
    const data = formatWeatherDataForChart(weatherData, fieldName, dateRange);
    const cumulativeData = calculateCumulative(data, fieldName);

    const returnValue = cumulativeData.map((item) => ({
      ...item,
      gdd: item[fieldName],
      cumulative_gdd: item[`cumulative_${fieldName}`],
      cumulative_gdd_prior: item[`cumulative_${fieldName}_prior`],
    }));

    return returnValue;
  }, [weatherData, dateRange, selectedCropType]);

  // Calculate summary statistics
  const totalPrecipitation = useMemo(() => {
    const filteredData = formatWeatherDataForChart(weatherData, WEATHER_FIELDS.PRECIPITATION, dateRange);
    const totalMm = calculateTotalPrecipitation(filteredData);
    // Convert total precipitation from mm to inches (1 inch = 25.4 mm)
    return totalMm / 25.4;
  }, [weatherData, dateRange]);

  const cumulativeGDD = useMemo(() => {
    if (!gddChartData.length) return 0;
    return gddChartData[gddChartData.length - 1]?.cumulative_gdd || 0;
  }, [gddChartData]);

  const averageTemperature = useMemo(() => {
    const filteredData = formatWeatherDataForChart(weatherData, WEATHER_FIELDS.MAX_TEMP, dateRange);
    return calculateAverageTemperature(filteredData);
  }, [weatherData, dateRange]);

  const currentGrowthStage = useMemo(() => {
    return getCurrentGrowthStage(cumulativeGDD, selectedCropType);
  }, [cumulativeGDD, selectedCropType]);

  const latestDroughtIndex = useMemo(() => {
    // Try to get the filtered data
    const filteredData = formatWeatherDataForChart(weatherData, WEATHER_FIELDS.Z_INDEX, dateRange);

    // Function to find latest item with valid PDSI
    const findLatestValidZIndex = (data) => {
      // Sort by date descending (most recent first)
      const sortedData = [...data].sort((a, b) => {
        return new Date(b[WEATHER_FIELDS.DATE]) - new Date(a[WEATHER_FIELDS.DATE]);
      });

      // Find the first (most recent) item with a valid PDSI value
      return sortedData.find(
        (item) => item[WEATHER_FIELDS.Z_INDEX] !== null && item[WEATHER_FIELDS.Z_INDEX] !== undefined
      );
    };

    // If we have filtered data, try to find latest valid PDSI
    if (filteredData.length > 0) {
      const latest = findLatestValidZIndex(filteredData);
      if (latest) {
        const result = {
          pdsi: latest[WEATHER_FIELDS.PDSI],
          z_index: latest[WEATHER_FIELDS.Z_INDEX],
          severity: getDroughtSeverityLabel(latest[WEATHER_FIELDS.Z_INDEX]),
        };
        return result;
      }
    }

    // If no filtered data or no valid PDSI in filtered data, try raw data
    if (weatherData.length > 0) {
      const latest = findLatestValidZIndex(weatherData);
      if (latest) {
        const result = {
          pdsi: latest[WEATHER_FIELDS.PDSI],
          z_index: latest[WEATHER_FIELDS.Z_INDEX],
          severity: getDroughtSeverityLabel(latest[WEATHER_FIELDS.Z_INDEX]),
        };
        return result;
      }
    }
    return null;
  }, [weatherData, dateRange]);

  // Calculate yield forecast values (separate for irrigated and non-irrigated)
  const yieldForecastValues = useMemo(() => {
    if (!yieldForecastData.length) return { irrigated: null, nonIrrigated: null };

    // Separate data by practice type
    const irrigatedData = yieldForecastData.filter((item) => item.practice_name === "Irrigated");
    const nonIrrigatedData = yieldForecastData.filter((item) => item.practice_name === "Non-Irrigated");

    const calculateWeightedAverage = (data) => {
      if (!data.length) return null;

      // Sort by Week_ending_date descending (most recent first)
      const sortedData = [...data].sort((a, b) => {
        return new Date(b.week_ending_date) - new Date(a.week_ending_date);
      });

      // Calculate exponential moving average with inverse exponential decay
      const alpha = 0.5; // Decay rate - adjust as needed (higher = faster decay)
      let weightedSum = 0;
      let weightSum = 0;

      sortedData.forEach((item, index) => {
        const weight = Math.exp(-alpha * index); // Exponential decay
        const yield_value = item.predicted_yield || 0;

        weightedSum += yield_value * weight;
        weightSum += weight;
      });

      return weightSum > 0 ? weightedSum / weightSum : 0;
    };

    return {
      irrigated: calculateWeightedAverage(irrigatedData),
      nonIrrigated: calculateWeightedAverage(nonIrrigatedData),
    };
  }, [yieldForecastData]);

  // Calculate RMA projected yield
  const rmaProjectedYieldIrrigated = useMemo(() => {
    return countyDataIrrigated?.expected_index_value || null;
  }, [countyDataIrrigated]);

  const rmaProjectedYieldNonIrrigated = useMemo(() => {
    return countyDataNonIrrigated?.expected_index_value || null;
  }, [countyDataNonIrrigated]);

  // Calculate RMA historical yield (actuals)
  const rmaHistoricalYieldIrrigated = useMemo(() => {
    return historicalYieldTrendDataIrrigated?.yield_amount || null;
  }, [historicalYieldTrendDataIrrigated]);

  const rmaHistoricalYieldNonIrrigated = useMemo(() => {
    return historicalYieldTrendDataNonIrrigated?.yield_amount || null;
  }, [historicalYieldTrendDataNonIrrigated]);

  // Return data and actions
  return {
    // Raw data
    weatherData,
    yieldForecastData,
    countyDataIrrigated,
    countyDataNonIrrigated,
    historicalYieldTrendDataIrrigated,
    historicalYieldTrendDataNonIrrigated,

    // Loading states
    isLoadingWeather,
    isLoadingYieldForecast,
    isLoadingCountyData,
    isLoadingHistoricalYieldTrend,

    // Chart data
    temperatureChartData,
    precipitationChartData,
    windChartData,
    droughtChartData,
    gddChartData,

    // Summary statistics
    totalPrecipitation,
    cumulativeGDD,
    averageTemperature,
    currentGrowthStage,
    latestDroughtIndex,
    yieldForecastValues,
    rmaProjectedYieldIrrigated,
    rmaProjectedYieldNonIrrigated,
    rmaHistoricalYieldIrrigated,
    rmaHistoricalYieldNonIrrigated,
    // Actions
    actions: {
      loadWeatherData,
      loadYieldForecastData,
      loadCountyData,
      loadHistoricalYieldTrend,
      handleError,
    },
  };
};
